
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { User, UserRole, Company } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { AuthContext } from '../../contexts/AuthContext';
import Spinner from '../../components/common/Spinner';
import ConfirmationModal from '../../components/common/ConfirmationModal';

const UserModal: React.FC<{
    userToEdit: User | null;
    tenantId: string;
    onClose: () => void;
    onSave: (user: Omit<User, 'uid' | 'createdAt' | 'tenantId'>, password?: string) => Promise<void>;
}> = ({ userToEdit, tenantId, onClose, onSave }) => {
    const [name, setName] = useState(userToEdit?.name || '');
    const [email, setEmail] = useState(userToEdit?.email || '');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState(userToEdit?.role || UserRole.CLIENT_VIEWER);
    const [active, setActive] = useState(userToEdit?.active ?? true);
    const [selectedCompanyId, setSelectedCompanyId] = useState(userToEdit?.companyId || '');
    
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const isEditing = !!userToEdit;

    // Cargar empresas de la agencia para poder asignar al usuario
    useEffect(() => {
        const fetchCompanies = async () => {
            setLoadingCompanies(true);
            try {
                const fetched = await firestoreService.getCompanies(tenantId);
                setCompanies(fetched);
                if (fetched.length > 0 && !selectedCompanyId && role === UserRole.CLIENT_VIEWER) {
                    // No pre-seleccionamos para obligar a elegir
                }
            } catch (e) {
                console.error("Error loading companies", e);
            } finally {
                setLoadingCompanies(false);
            }
        };
        fetchCompanies();
    }, [tenantId, role]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        if (role === UserRole.CLIENT_VIEWER && !selectedCompanyId) {
            setError("Debes asignar una Empresa a este cliente viewer.");
            return;
        }

        if (!isEditing && password.length < 6) {
            setError("La contraseña debe tener al menos 6 caracteres.");
            return;
        }

        setIsSubmitting(true);
        const userData: any = { 
            name, 
            email, 
            role, 
            active,
            companyId: role === UserRole.CLIENT_VIEWER ? selectedCompanyId : "" 
        };
        
        try {
            await onSave(userData, isEditing ? undefined : password);
            onClose();
        } catch (err: any) {
            setError(err.message || "Error al guardar el usuario.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
      <div className="fixed inset-0 bg-black/85 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-800 animate-fade-in-scale">
              <h2 className="text-2xl font-bold text-white mb-6">{isEditing ? 'Editar Perfil' : 'Nuevo Usuario'}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre Completo</label>
                      <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                   <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white disabled:opacity-50" required disabled={isEditing} />
                  </div>
                  
                  {!isEditing && (
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Contraseña</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white outline-none focus:ring-2 focus:ring-blue-500" required minLength={6} />
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 pt-2">
                      <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Rol en la Agencia</label>
                          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white outline-none">
                              <option value={UserRole.ANALYST}>Analista (Acceso total Agencia)</option>
                              <option value={UserRole.CLIENT_VIEWER}>Cliente Viewer (Solo su empresa)</option>
                              <option value={UserRole.TENANT_ADMIN}>Admin de Agencia (Gobernanza)</option>
                          </select>
                      </div>

                      {role === UserRole.CLIENT_VIEWER && (
                          <div className="animate-fade-in">
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Empresa Asignada</label>
                              {loadingCompanies ? <Spinner size="sm" /> : (
                                  <select 
                                      value={selectedCompanyId} 
                                      onChange={(e) => setSelectedCompanyId(e.target.value)} 
                                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white outline-none border-blue-500/30"
                                      required={role === UserRole.CLIENT_VIEWER}
                                  >
                                      <option value="">-- Seleccionar Empresa Cliente --</option>
                                      {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                                  </select>
                              )}
                          </div>
                      )}
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                      <input type="checkbox" id="u-active" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-5 h-5 bg-gray-800 border-gray-700 rounded text-blue-600" />
                      <label htmlFor="u-active" className="text-sm text-gray-300">Usuario Activo</label>
                  </div>

                  {error && <p className="text-red-400 text-xs bg-red-900/10 p-2 rounded border border-red-900/20">{error}</p>}
                  
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
                      <button type="button" onClick={onClose} className="px-5 py-2 text-gray-400 hover:text-white">Cancelar</button>
                      <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 shadow-lg shadow-blue-900/20">
                           {isSubmitting ? <Spinner size="sm" /> : (isEditing ? 'Actualizar' : 'Crear Usuario')}
                      </button>
                  </div>
              </form>
          </div>
      </div>
    );
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const tenantId = currentUser?.tenantId;

  const fetchUsers = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
        const fetchedUsers = await firestoreService.getUsers(tenantId);
        setUsers(fetchedUsers);
    } catch (error) {
        console.error("Error fetching users:", error);
    } finally {
        setLoading(false);
    }
  }, [tenantId]);
  
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenModal = (user: User | null) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleSaveUser = async (userData: Omit<User, 'uid' | 'createdAt' | 'tenantId'>, password?: string) => {
    if (!tenantId) return;
    
    try {
        if (editingUser) {
            await firestoreService.updateUser(editingUser.uid, { ...userData });
        } else if (password) {
            await firestoreService.registerUser({ 
              ...userData, 
              tenantId 
            }, password);
        }
        fetchUsers();
    } catch (error: any) {
        throw error;
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
        await firestoreService.deleteUser(userToDelete.uid);
        fetchUsers();
        setUserToDelete(null);
    } catch (error: any) {
        alert(`Error: ${error.message}`);
    } finally {
        setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
            <h1 className="text-3xl font-bold text-white">Gestión de Equipo</h1>
            <p className="text-gray-400">Administra los accesos de tus analistas y clientes.</p>
        </div>
        <button 
          onClick={() => handleOpenModal(null)}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold shadow-lg hover:bg-blue-500 transition-all">
          + Nuevo Usuario
        </button>
      </div>

      <div className="bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-800 min-h-[300px] flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
        ) : users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Rol</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {users.map((u) => (
                  <tr key={u.uid} className="hover:bg-gray-800/40 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                            <span className="text-sm text-white font-bold">{u.name}</span>
                            <span className="text-xs text-gray-500">{u.email}</span>
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-tighter ${u.role === UserRole.CLIENT_VIEWER ? 'bg-purple-900/20 text-purple-400' : 'bg-blue-900/20 text-blue-400'}`}>
                        {u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                      <button onClick={() => handleOpenModal(u)} className="text-blue-400 hover:text-blue-300 font-bold">Editar</button>
                      <button onClick={() => setUserToDelete(u)} className="text-red-400 hover:text-red-300">Eliminar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-20 text-gray-600">
            No hay usuarios registrados.
          </div>
        )}
      </div>
      {isModalOpen && <UserModal userToEdit={editingUser} tenantId={tenantId || ''} onClose={() => setIsModalOpen(false)} onSave={handleSaveUser} />}
      <ConfirmationModal isOpen={!!userToDelete} onClose={() => setUserToDelete(null)} onConfirm={confirmDeleteUser} title="Eliminar Usuario" message={<>¿Deseas eliminar a <strong>{userToDelete?.name}</strong>?</>} isConfirming={isDeleting} />
    </div>
  );
};

export default UsersPage;
